import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PaperPlaneRight, ChatsCircle, ArrowLeft } from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SocialPulseBadge from "@/components/SocialPulseBadge";

function timeago(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function DirectMessages() {
  useSeo("Messages", null, null, { noindex: true });
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const active = params.get("c");
  const [convos, setConvos] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadConvos = () => api.get("/dm/conversations").then((r) => setConvos(r.data.conversations)).catch(() => setConvos([]));
  useEffect(() => { loadConvos(); }, []);
  useEffect(() => {
    const t = setInterval(loadConvos, 15000);
    return () => clearInterval(t);
  }, []);

  const openThread = (cid) => {
    setParams({ c: cid });
    api.get(`/dm/conversations/${cid}/messages`).then((r) => setThread(r.data)).catch(() => toast.error("Couldn't load messages"));
  };
  useEffect(() => {
    if (active) {
      api.get(`/dm/conversations/${active}/messages`).then((r) => setThread(r.data)).catch(() => setThread(null));
    } else {
      setThread(null);
    }
    // eslint-disable-next-line
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !active) return;
    setSending(true);
    setText("");
    try {
      const { data } = await api.post(`/dm/conversations/${active}/messages`, { text: t });
      setThread((prev) => prev ? { ...prev, messages: [...prev.messages, data] } : prev);
      loadConvos();
    } catch (err) {
      toast.error("Message not sent");
      setText(t);
    } finally { setSending(false); }
  };

  if (!user) return <div className="mx-auto max-w-md px-5 py-24 text-center text-muted-foreground">Please sign in to view your messages.</div>;

  return (
    <div className="mx-auto max-w-5xl px-3 py-4 sm:px-5 sm:py-8">
      <h1 className="mb-4 font-heading text-2xl font-medium tracking-tight">Messages</h1>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[320px_1fr]">
        {/* Conversation list */}
        <div className={`rounded-2xl border border-border/60 bg-white ${active ? "hidden md:block" : "block"}`} data-testid="dm-conversation-list">
          {convos === null ? (
            <div className="p-4 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-muted" />)}</div>
          ) : convos.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-16 text-center">
              <ChatsCircle size={40} className="text-muted-foreground/50" />
              <p className="mt-3 font-medium">No conversations yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Open an alumnus's profile and tap Message to start chatting.</p>
              <Link to="/directory" className="mt-4"><Button variant="outline" className="rounded-full">Browse alumni</Button></Link>
            </div>
          ) : (
            <ul className="divide-y divide-border/50">
              {convos.map((c) => (
                <li key={c.id}>
                  <button onClick={() => openThread(c.id)} data-testid={`dm-convo-${c.id}`}
                    className={`flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-accent/50 ${active === c.id ? "bg-accent/60" : ""}`}>
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarImage src={c.other.avatar ? fileUrl(c.other.avatar) : undefined} />
                      <AvatarFallback>{(c.other.name || "M")[0]}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <span className="truncate text-sm font-semibold">{c.other.name}</span>
                        <SocialPulseBadge verified={c.other.socialpulse_verified} showLabel={false} />
                        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{timeago(c.last_at)}</span>
                      </div>
                      <p className={`truncate text-xs ${c.unread ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                        {c.last_sender === user.id ? "You: " : ""}{c.last_text}
                      </p>
                    </div>
                    {c.unread && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-primary" data-testid="dm-unread-dot" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Thread */}
        <div className={`flex min-h-[60vh] flex-col rounded-2xl border border-border/60 bg-white ${active ? "flex" : "hidden md:flex"}`} data-testid="dm-thread">
          {!thread ? (
            <div className="flex flex-1 flex-col items-center justify-center px-6 text-center text-muted-foreground">
              <ChatsCircle size={44} className="text-muted-foreground/40" />
              <p className="mt-3 text-sm">Select a conversation to start chatting.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3">
                <button className="md:hidden" onClick={() => setParams({})} data-testid="dm-back"><ArrowLeft size={20} /></button>
                <Avatar className="h-9 w-9"><AvatarImage src={thread.other.avatar ? fileUrl(thread.other.avatar) : undefined} /><AvatarFallback>{(thread.other.name || "M")[0]}</AvatarFallback></Avatar>
                <div className="min-w-0">
                  <div className="flex items-center gap-1">
                    {thread.other.slug ? <Link to={`/u/${thread.other.slug}`} className="truncate text-sm font-semibold hover:underline">{thread.other.name}</Link> : <span className="truncate text-sm font-semibold">{thread.other.name}</span>}
                    <SocialPulseBadge verified={thread.other.socialpulse_verified} showLabel={false} />
                  </div>
                  {thread.other.headline && <p className="truncate text-xs text-muted-foreground">{thread.other.headline}</p>}
                </div>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4" data-testid="dm-messages">
                {thread.messages.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">Say hello 👋</p>}
                {thread.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${m.mine ? "bg-primary text-primary-foreground" : "bg-accent text-foreground"}`}>
                      <p className="whitespace-pre-wrap break-words">{m.text}</p>
                      <p className={`mt-0.5 text-[10px] ${m.mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{timeago(m.created_at)}</p>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="flex items-center gap-2 border-t border-border/60 p-3" data-testid="dm-composer">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message…" className="rounded-full" data-testid="dm-input" maxLength={4000} />
                <Button type="submit" disabled={sending || !text.trim()} className="shrink-0 rounded-full" data-testid="dm-send"><PaperPlaneRight size={16} weight="fill" /></Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
